/** @module The collection: every owned copy as tiles or a table, with filters and bulk actions. */
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowDownUp, LayoutGrid, Search, SquareCheck, Table, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { ItemSheet } from "@/components/item-sheet";
import { PageHeader } from "@/components/page-header";
import { Segmented } from "@/components/ui/controls";
import { BulkBar } from "@/features/collection/bulk-bar";
import { DataSheet } from "@/features/collection/data-sheet";
import {
  activeCount,
  EMPTY,
  type Filters,
  toQuery,
} from "@/features/collection/collection-filters";
import {
  GridView,
  type Item,
  type Row,
  rowOfItem,
  rowOfVariant,
  TableView,
} from "@/features/collection/collection-views";
import { FilterRow } from "@/features/collection/filter-sheet";
import { count, money } from "@/lib/format";
import { useSummary } from "@/lib/review-count";

type View = "grid" | "table";
type Grouping = "variant" | "copy";

/** Remembering the view is the whole point of a toggle nobody wants to press twice. */
const VIEW_KEY = "poke-scan.collection.view";
const GROUP_KEY = "poke-scan.collection.group";

interface Page {
  rows: Row[];
  next_cursor?: string | null;
  total?: number | null;
}

async function fetchPage(filters: Filters, grouping: Grouping, cursor: string | null) {
  const query = { ...toQuery(filters, cursor), with_total: !cursor };
  if (grouping === "variant") {
    const { data, response } = await api.GET("/api/v1/collection/variants", {
      params: { query },
    });
    if (!data) throw problemFrom(response);
    return { ...data, rows: data.items.map(rowOfVariant) } satisfies Page;
  }
  const { data, response } = await api.GET("/api/v1/collection/items", { params: { query } });
  if (!data) throw problemFrom(response);
  return { ...data, rows: data.items.map(rowOfItem) } satisfies Page;
}

function remembered<T extends string>(key: string, options: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return options.find((option) => option === value) ?? fallback;
  } catch {
    // Private windows can refuse storage; the default still works.
    return fallback;
  }
}

function remember(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not being able to remember a view is not worth an error message.
  }
}

export function CollectionPage() {
  const {
    status: initialStatus,
    purchase: initialPurchase,
    location: initialLocation,
  } = useSearch({ strict: false });
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<Filters>(() =>
    // "Archiv" on the "Mehr" page opens the list on the archived copies.
    // The start screen links to the copies that still lack a purchase price.
    ({
      ...EMPTY,
      status: initialStatus === "archived" ? "archived" : "active",
      purchase: initialPurchase ?? null,
      location: initialLocation ?? null,
    }),
  );
  const navigate = useNavigate();
  const [view, setView] = useState<View>(() => remembered(VIEW_KEY, ["grid", "table"], "grid"));
  // Per variant as in the design; per copy for sorting out single copies.
  const [grouping, setGrouping] = useState<Grouping>(() =>
    remembered(GROUP_KEY, ["variant", "copy"], "variant"),
  );
  const [open, setOpen] = useState<Item | null>(null);
  const [transferring, setTransferring] = useState(false);
  // Null means "not selecting": a tap opens the copy instead of marking it.
  const [selection, setSelection] = useState<Set<string> | null>(null);
  const summary = useSummary();

  const page = useInfiniteQuery({
    queryKey: ["collection", grouping, filters],
    queryFn: ({ pageParam }) => fetchPage(filters, grouping, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? null,
  });

  const rows = page.data?.pages.flatMap((one) => one.rows) ?? [];
  const total = page.data?.pages[0]?.total ?? null;
  const filtered = activeCount(filters) > 0;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["collection"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    void queryClient.invalidateQueries({ queryKey: ["set-progress"] });
  };
  const pick = (row: Row) => {
    if (!selection) {
      // A single copy opens right here; a variant with several copies lists them on its card.
      if (row.item) setOpen(row.item);
      else void navigate({ to: "/cards/$printId", params: { printId: row.printId } });
      return;
    }
    setSelection((current) => {
      const next = new Set(current);
      if (next.has(row.key)) next.delete(row.key);
      else next.add(row.key);
      return next;
    });
  };
  const toggleAll = () => {
    setSelection((current) =>
      current?.size === rows.length ? new Set() : new Set(rows.map((row) => row.key)),
    );
  };
  const chooseView = (next: View) => {
    setView(next);
    remember(VIEW_KEY, next);
  };
  const chooseGrouping = (next: Grouping) => {
    setGrouping(next);
    setSelection((current) => (current ? new Set() : null));
    remember(GROUP_KEY, next);
  };
  // The bulk actions work on copies: a selected variant stands for all of its copies.
  const selectedIds = rows.filter((row) => selection?.has(row.key)).flatMap((row) => row.ids);

  // The whole collection's value comes from the server; a sum of the loaded page would lie.
  let subtitle: string | null = null;
  if (filtered && total !== null) {
    subtitle = t("collection.matches", { count: total, shown: count(total, locale) });
  } else if (summary.data) {
    subtitle = t("collection.subtitle", {
      cards: count(summary.data.item_count, locale),
      value: money(summary.data.total_value, locale) ?? "–",
    });
  }

  const viewOptions = (labels: boolean) => [
    {
      value: "grid" as const,
      label: labels ? t("collection.grid") : "",
      icon: <LayoutGrid />,
      ariaLabel: t("collection.grid"),
    },
    {
      value: "table" as const,
      label: labels ? t("collection.table") : "",
      icon: <Table />,
      ariaLabel: t("collection.table"),
    },
  ];

  const header = selection ? (
    <PageHeader
      global={false}
      back={
        <button
          type="button"
          className="icon-btn"
          aria-label={t("collection.endSelection")}
          onClick={() => {
            setSelection(null);
          }}
        >
          <X />
        </button>
      }
      title={t("collection.bulk.selected", { count: selectedIds.length })}
      actions={
        <button type="button" className="btn btn-ghost btn-sm" onClick={toggleAll}>
          {t("collection.selectAll")}
        </button>
      }
    />
  ) : (
    <PageHeader
      title={t("nav.collection")}
      subtitle={subtitle}
      numeric
      actions={
        <>
          <span className="hidden md:block">
            <Segmented
              label={t("collection.view")}
              value={view}
              onChange={chooseView}
              options={viewOptions(true)}
            />
          </span>
          <button
            type="button"
            className="icon-btn"
            aria-label={t("transfer.menu")}
            title={t("transfer.menu")}
            onClick={() => {
              setTransferring(true);
            }}
          >
            <ArrowDownUp />
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label={t("collection.select")}
            title={t("collection.select")}
            onClick={() => {
              setSelection(new Set());
            }}
          >
            <SquareCheck />
          </button>
        </>
      }
    />
  );

  return (
    <div className={`flex flex-col gap-3 md:gap-4 ${selection ? "pb-24" : ""}`}>
      {header}

      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <label className="input-search md:w-80">
          <Search />
          <input
            type="search"
            value={filters.query}
            onChange={(event) => {
              setFilters((current) => ({ ...current, query: event.target.value }));
            }}
            placeholder={t("collection.searchPlaceholder")}
            aria-label={t("collection.search")}
            className="input"
          />
        </label>
        <div className="row-between min-w-0 gap-3 md:grow">
          <FilterRow
            filters={filters}
            update={setFilters}
            grouping={grouping}
            onGrouping={chooseGrouping}
          />
          <span className="md:hidden">
            <Segmented
              label={t("collection.view")}
              value={view}
              onChange={chooseView}
              options={viewOptions(false)}
            />
          </span>
        </div>
      </div>

      {page.isPending ? <p className="t-muted">{t("common.loading")}</p> : null}
      {page.isError ? (
        <p role="alert" className="loss">
          {t("common.loadError")}
        </p>
      ) : null}
      {page.isSuccess && rows.length === 0 ? (
        <p className="t-muted">{filtered ? t("collection.noMatch") : t("collection.empty")}</p>
      ) : null}

      {view === "grid" ? (
        <GridView rows={rows} selected={selection ?? undefined} onPick={pick} />
      ) : (
        <TableView
          rows={rows}
          grouped={grouping === "variant"}
          selected={selection ?? undefined}
          onPick={pick}
          onToggleAll={toggleAll}
        />
      )}

      {page.hasNextPage ? (
        <button
          type="button"
          disabled={page.isFetchingNextPage}
          onClick={() => {
            void page.fetchNextPage();
          }}
          className="btn btn-secondary self-center"
        >
          {page.isFetchingNextPage ? t("common.loading") : t("collection.more")}
        </button>
      ) : null}
      {total !== null && rows.length > 0 ? (
        <p className="t-xs t-muted self-center">
          {t("collection.ofTotal", {
            shown: count(rows.length, locale),
            total: count(total, locale),
          })}
        </p>
      ) : null}

      {selection && selectedIds.length > 0 ? (
        <BulkBar
          ids={selectedIds}
          archived={filters.status === "archived"}
          onDone={() => {
            setSelection(new Set());
            refresh();
          }}
          onClear={() => {
            setSelection(new Set());
          }}
        />
      ) : null}

      {open ? (
        <ItemSheet
          item={open}
          onClose={() => {
            setOpen(null);
          }}
          onChanged={() => {
            setOpen(null);
            refresh();
          }}
        />
      ) : null}

      <DataSheet
        open={transferring}
        onClose={() => {
          setTransferring(false);
        }}
        filters={filters}
      />
    </div>
  );
}
